import React, { useState } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';
import axios from 'axios';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import useDebounce from 'components/hooks/useDebounce';

import CollectionSelect from 'components/application/CollectionSelect';

import {
  formatInfiniteData,
  getNextPageParam,
} from 'components/helpers/reactQuery';
import { resourceShape } from 'components/helpers/serialisableResources';
import { personDisplayName } from 'components/helpers/users';

const assignableLineManagerOption = (personnel) => ({
  label: personDisplayName({ attributes: personnel.attributes }),
  value: personnel.id,
});
export default function SelectLineManager({
  currentPersonnel,
  domainLineManager,
  isDisabled,
  onChange,
}) {
  const [inputValue, setInputValue] = useState('');
  const [debouncedInputValue] = useDebounce(inputValue, 250);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedLineManagerId, setSelectedLineManagerId] = useState(
    domainLineManager?.id,
  );

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'personnel.SelectLineManager',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const {
    data: domainAssignableLineManagers,
    fetchNextPage: fetchDomainAssignableLineManagersNextPage,
    hasNextPage: hasDomainAssignableLineManagersNextPage,
    isFetching: isDomainAssignableLineManagersFetching,
  } = useInfiniteQuery({
    enabled: isMenuOpen,
    queryKey: ['personnel', { search: debouncedInputValue }],
    queryFn: async ({ pageParam = 1 }) => {
      const params = {
        assignable_personnel_search: {
          filter: { incl_employees: true },
          page: pageParam,
          search: debouncedInputValue,
        },
      };

      if (currentPersonnel.id) {
        params.assignable_personnel_search.filter.ids = {
          nin: [currentPersonnel.id],
        };
      }

      const response = await axios.post(
        '/assignable_personnel_searches',
        params,
      );

      return response.data;
    },
    getNextPageParam,
    select: (data) => formatInfiniteData({ data }),
  });

  const handleInputChange = (val, { action }) => {
    if (action === 'input-change') setInputValue(val);
  };

  const handleMenuOpen = () => setIsMenuOpen(true);

  const handleChange = (selected, actionMeta) => {
    setSelectedLineManagerId(selected?.value || null);

    onChange(selected, actionMeta);
  };

  const assignableLineManagers = (() => {
    if (!domainAssignableLineManagers) return [];
    if (!selectedLineManagerId) return domainAssignableLineManagers.data;

    return domainAssignableLineManagers.data.filter(
      (assignableLineManager) =>
        assignableLineManager.id !== selectedLineManagerId,
    );
  })();

  const options = assignableLineManagers.map((assignableLineManager) =>
    assignableLineManagerOption(assignableLineManager),
  );

  const isDomainLineManagerSelected =
    !!selectedLineManagerId && selectedLineManagerId === domainLineManager?.id;

  const value =
    selectedLineManagerId ?
      isDomainLineManagerSelected ?
        assignableLineManagerOption(domainLineManager)
      : options.find((option) => option.value === selectedLineManagerId)
    : null;

  return (
    <CollectionSelect
      externalProps={{
        isLoading:
          debouncedInputValue !== inputValue ||
          isDomainAssignableLineManagersFetching,
        onInputChange: handleInputChange,
        onMenuOpen: handleMenuOpen,
        onMenuScrollToBottom: () => {
          if (hasDomainAssignableLineManagersNextPage) {
            fetchDomainAssignableLineManagersNextPage();
          }
        },
      }}
      isClearable={true}
      isDisabled={isDisabled}
      isSearchable={true}
      label={i18nTShared('lineManager')}
      modifiers={['typeable-search']}
      name='lineManagerId'
      onChange={handleChange}
      options={options}
      tooltip={i18nT('tooltip')}
      value={value}
    />
  );
}

SelectLineManager.propTypes = {
  isDisabled: PropTypes.bool.isRequired,
  currentPersonnel: PropTypes.object.isRequired,
  domainLineManager: resourceShape('lineManager'),
  onChange: PropTypes.func.isRequired,
};
